// src/pages/Receiver/GHG/components/ExportPptDialog.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  Typography,
  Grid,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export default function ExportPptDialog({
  open,
  onClose,
  hasSearched,
  reportFromStr,
  reportToStr,
  setReportFromStr,
  setReportToStr,
  onExport,
}) {
  // Draft date state for modal (strings YYYY-MM-DD; blank => all time)
  const [exportFromDraft, setExportFromDraft] = useState("");
  const [exportToDraft, setExportToDraft] = useState("");

  useEffect(() => {
    if (!open) return;
    setExportFromDraft(reportFromStr || "");
    setExportToDraft(reportToStr || "");
  }, [open, reportFromStr, reportToStr]);

  const [pptOptions, setPptOptions] = useState({
    includeTitleSlide: true,
    includeAvoidedTrend: true,
    includePickups: true,
    includeSankeyQty: true,
    includeSankeyAvoided: true,
    includeMaterialsTable: true,
    tableRowsPerSlide: 14,
  });

  const toggleOpt = (key) => setPptOptions((p) => ({ ...p, [key]: !p[key] }));

  const toDayjsOrNull = (s) => {
    if (!s) return null;
    const d = dayjs(s, "YYYY-MM-DD", true);
    return d.isValid() ? d : null;
  };

  const validateDateStr = (s) => {
    if (!s) return true;
    const d = dayjs(s, "YYYY-MM-DD", true);
    return d.isValid();
  };

  const dateError = useMemo(() => {
    return (
      (exportFromDraft && !validateDateStr(exportFromDraft)) ||
      (exportToDraft && !validateDateStr(exportToDraft))
    );
  }, [exportFromDraft, exportToDraft]);

  const waitNextPaint = () =>
    new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export PPT — Options</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Choose date range + which slides to include.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Report date range
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="From"
                value={toDayjsOrNull(exportFromDraft)}
                onChange={(v) => setExportFromDraft(v ? v.format("YYYY-MM-DD") : "")}
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    helperText:
                      exportFromDraft && !validateDateStr(exportFromDraft) ? "Invalid" : " ",
                    error: Boolean(exportFromDraft) && !validateDateStr(exportFromDraft),
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="To"
                value={toDayjsOrNull(exportToDraft)}
                onChange={(v) => setExportToDraft(v ? v.format("YYYY-MM-DD") : "")}
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    helperText:
                      exportToDraft && !validateDateStr(exportToDraft) ? "Invalid" : " ",
                    error: Boolean(exportToDraft) && !validateDateStr(exportToDraft),
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
          Leave both empty for <b>All time</b>.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Slides to include
        </Typography>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={pptOptions.includeTitleSlide}
                onChange={() => toggleOpt("includeTitleSlide")}
              />
            }
            label="Title + KPIs slide"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={pptOptions.includeAvoidedTrend}
                onChange={() => toggleOpt("includeAvoidedTrend")}
              />
            }
            label="Avoided emissions trend chart"
          />
          <FormControlLabel
            control={
              <Checkbox checked={pptOptions.includePickups} onChange={() => toggleOpt("includePickups")} />
            }
            label="Pickups chart"
          />

          <Divider sx={{ my: 1.5 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={pptOptions.includeSankeyQty}
                onChange={() => toggleOpt("includeSankeyQty")}
              />
            }
            label="Sankey — Quantity"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={pptOptions.includeSankeyAvoided}
                onChange={() => toggleOpt("includeSankeyAvoided")}
              />
            }
            label="Sankey — Avoided emissions"
          />

          <Divider sx={{ my: 1.5 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={pptOptions.includeMaterialsTable}
                onChange={() => toggleOpt("includeMaterialsTable")}
              />
            }
            label="Materials table"
          />
        </FormGroup>

        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <TextField
            label="Table rows per slide"
            size="small"
            type="number"
            value={pptOptions.tableRowsPerSlide}
            onChange={(e) =>
              setPptOptions((p) => ({
                ...p,
                tableRowsPerSlide: Number(e.target.value || 14),
              }))
            }
            inputProps={{ min: 4, max: 30 }}
            disabled={!pptOptions.includeMaterialsTable}
          />
        </Box>

        {dateError ? (
          <Box sx={{ mt: 2 }}>
            <Typography color="error" variant="body2">
              Fix date fields or clear them.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={Boolean(dateError) || !hasSearched}
          onClick={async () => {
            // Commit chosen dates to page state so memo calculations match export
            setReportFromStr(String(exportFromDraft || "").trim());
            setReportToStr(String(exportToDraft || "").trim());

            await waitNextPaint();

            onClose?.();
            await onExport?.(pptOptions);
          }}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
